// https://github.com/gpbl/isomorphic500/blob/master/src/utils/IntlUtils.js

// Contains utils to download the locale data for the current language, eventually
// requiring the `Intl` polyfill for browser not supporting it
// It is used in client.js *before* rendering the root component.
import countries from "i18n-iso-countries";

import IntPoly from "intl";
const OriginalIntl = global.Intl;

function areIntlLocalesSupported(locales, intlObj) {
  if (!Array.isArray(locales)) {
    locales = [locales];
  }

  if (!intlObj) {
    intlObj = global.Intl;
  }

  let intlConstructors = [intlObj.DateTimeFormat, intlObj.NumberFormat, intlObj.PluralRules];

  intlConstructors = intlConstructors.filter(Boolean);

  return intlConstructors.every(intlConstructor => {
    try {
      return intlConstructor.supportedLocalesOf(locales).length === locales.length;
    } catch (e) {
      console.log("Failed to check supported locales.", e);
      return false;
    }
  });
}

// Returns a promise which is resolved when Intl has been polyfilled
export async function loadPolyfill(locale) {
  if (!areIntlLocalesSupported(locale, OriginalIntl)) {
    global.Intl = IntPoly;
  }
}

// Returns a promise which is resolved as the required locale-data chunks
// has been downloaded with webpack's lazy load. For each language,
// The react-intl locale-data is required, for example, by the FormattedRelative
// component.
export async function loadLocaleData(locale) {
  // do not remove code duplication (because Webpack won't work as expected)
  switch (locale) {
    // euskera
    case "eu":
      if (!areIntlLocalesSupported("eu")) {
        await import("intl/locale-data/jsonp/eu.js");
      }
      break;
    // spanish
    case "es":
      if (!areIntlLocalesSupported("es")) {
        await import("intl/locale-data/jsonp/es.js");
      }
      break;
    // catalan
    case "ca":
      if (!areIntlLocalesSupported("ca")) {
        await import("intl/locale-data/jsonp/ca.js");
      }
      break;
    // deutch
    case "de":
      if (!areIntlLocalesSupported("de")) {
        await import("intl/locale-data/jsonp/de.js");
      }
      break;
    // french
    case "fr":
      if (!areIntlLocalesSupported("fr")) {
        await import("intl/locale-data/jsonp/fr.js");
      }
      break;

    // italian
    case "it":
      if (!areIntlLocalesSupported("it")) {
        await import("intl/locale-data/jsonp/it.js");
      }
      break;

    // dutch
    case "nl":
      if (!areIntlLocalesSupported("nl")) {
        await import("intl/locale-data/jsonp/nl.js");
      }
      break;
    // Korean
    case "ko":
      if (!areIntlLocalesSupported("ko")) {
        await import("intl/locale-data/jsonp/ko.js");
      }
      break;

    // portuguese
    case "pt":
      if (!areIntlLocalesSupported("pt")) {
        await import("intl/locale-data/jsonp/pt.js");
      }
      break;

    // russian
    case "ru":
      if (!areIntlLocalesSupported("ru")) {
        await import("intl/locale-data/jsonp/ru.js");
      }
      break;

    // Danish
    case "da":
      if (!areIntlLocalesSupported("da")) {
        await import("intl/locale-data/jsonp/da.js");
      }
      break;

    // english
    default:
      if (!areIntlLocalesSupported("en-GB")) {
        await import("intl/locale-data/jsonp/en-GB.js");
      }
  }
}

export function loadPolyfillAndData(locale) {
  return loadPolyfill(locale).then(loadLocaleData.bind(null, locale));
}

countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
countries.registerLocale(require("i18n-iso-countries/langs/es.json"));

// Our API does not recognize Kosovo as a country ¯\_(ツ)_/¯
export const countryCodes = Object.keys(countries.getAlpha2Codes()).filter(it => it !== "XK");

export function getTranslatedCountryList(locale) {
  return (
    countryCodes.map(it => ({
      label: `${countries.getName(it, locale, { select: "official" })}`,
      value: it,
    })) || []
  );
}
